import React from 'react';
import { BodyText, Caption, resetButtonStyle } from '@kvdbil/components';
import styled from 'styled-components';
import { scroller } from 'react-scroll';
import { useSelector } from 'react-redux';
import { LeasingPartnerNavigationFragment } from '~/config/generated/graphql';
import { slugify } from '~/helpers/slugify';
import PartnerLogo from './PartnerLogo';
import { headerHeightSelector } from '~/App/shared/selectors/header';
import { useIsDevice } from '~/App/shared/hooks/useIsDevice';
import { useTranslation } from '~/Locale';

const BLOCK_SPACING = '2.5rem';

const LOGO_HEIGHT = '3rem';

const Container = styled.div`
  margin: ${BLOCK_SPACING} 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.dark};
`;

const LogoList = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 1rem 2rem;
`;

const LogoButton = styled.button`
  ${resetButtonStyle};
  display: block;
  cursor: pointer;
`;

const Logo = styled(PartnerLogo)`
  height: ${LOGO_HEIGHT};
`;

type Props = Pick<
  LeasingPartnerNavigationFragment,
  'title' | 'helpText' | 'partnerLogos'
>;

type RenderablePartnerLogo = Props['partnerLogos'][number] & {
  scrollTargetId: string;
};

const LeasingPartnerNavigation = ({ title, helpText, partnerLogos }: Props) => {
  const headerHeight = useSelector(headerHeightSelector) ?? 0;
  const { isMobile } = useIsDevice();
  const { t } = useTranslation();

  const items = partnerLogos.filter(
    (item): item is RenderablePartnerLogo =>
      Boolean(item.scrollTargetId) && Boolean(item.logo)
  );

  if (items.length === 0) {
    return null;
  }

  const handleClick = (scrollTargetId: string) => () => {
    const target = slugify(scrollTargetId);

    scroller.scrollTo(target, {
      duration: 400,
      smooth: true,
      dynamic: true,
      offset: ((isMobile ? 0 : headerHeight) + 32) * -1
    });
  };

  return (
    <Container>
      {title && <BodyText>{title}</BodyText>}

      <LogoList>
        {items.map(({ id, scrollTargetId, logo }) => {
          const image = logo?.responsiveImage;

          return (
            <LogoButton
              key={id}
              type="button"
              onClick={handleClick(scrollTargetId)}
              aria-label={image?.alt ? undefined : t('Show partner offers')}
            >
              <Logo logo={logo} />
            </LogoButton>
          );
        })}
      </LogoList>

      {helpText && <Caption>{helpText}</Caption>}
    </Container>
  );
};

export default LeasingPartnerNavigation;
